import {
  HttpParameterCodec,
  HttpParams,
  httpResource,
  HttpResourceRef,
} from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import {
  ApiHttpResponse,
  Coaching,
  Country,
  GpsOnly,
  Playlist,
  Tag,
  VideoSection,
  Workout,
} from '@kinomap-dev/ng-ui-lib/core';
import { environment } from '../../environments/environment.development';
import { Vehicule } from '../types/vehicule.types';

import { SortDirectionType } from '../types/sort-direction.type';
import { RawUploadContentType, UploadContentType } from '../types/upload-content.type';
import { ContentType } from '../pages/content-creation/content-creation';
import { groupViewerRepartition } from '../utils/group-viewer-repartition-data';
import { ModerationReason } from '../types/moderation-reason.type';
import { ContentStatistics } from '../types/content-statistics.type';
import {
  ViewerRepartitionItem,
  ContentCountryItem,
} from '../types/country-viewer-repartition.type';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  getVehiculesRessouces: HttpResourceRef<Vehicule[] | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}vehicles`,
      method: 'GET',
    }),
    {
      parse: (raw: unknown) => (raw as { data: Vehicule[] }).data,
    },
  );

  getTagsRessources: HttpResourceRef<Tag[] | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}tag`,
      method: 'GET',
    }),
    {
      parse: (raw: unknown) => (raw as { data: Tag[] }).data,
    },
  );

  getProfileRessources: HttpResourceRef<any | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}me`,
      method: 'GET',
    }),
    { parse: (raw: unknown) => (raw as any).data },
  );

  getSettingsRessources: HttpResourceRef<any | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}me/settings`,
      method: 'GET',
    }),
    {
      parse: (raw: unknown) => (raw as any).data,
    },
  );

  getCountryRessources: HttpResourceRef<Country[] | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}countries/list`,
      method: 'GET',
    }),
    { parse: (value: unknown) => (value as ApiHttpResponse<Country[]>).data },
  );

  getScenicContentRessources(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
    term?: Signal<string | undefined>,
  ): HttpResourceRef<VideoSection[] | undefined> {
    return httpResource(
      () => {
        const searchTerm = term?.();
        if (!searchTerm) return undefined;
        const params: HttpParams = this.buildContentParams(page, limit, sortDirection, searchTerm);
        return {
          url: `${environment.apiUrl}me/scenics`,
          method: 'GET',
          params,
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<VideoSection[]>).data,
      },
    );
  }

  getWorkoutContentsRessources(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
    term?: Signal<string | undefined>,
  ): HttpResourceRef<Workout[] | undefined> {
    return httpResource(
      () => {
        const searchTerm = term?.();
        if (!searchTerm) return undefined;
        const params: HttpParams = this.buildContentParams(page, limit, sortDirection, searchTerm);
        return {
          url: `${environment.apiUrl}me/workouts`,
          method: 'GET',
          params,
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<Workout[]>).data,
      },
    );
  }

  getGpsOnlyContentsRessources(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
    term?: Signal<string | undefined>,
  ): HttpResourceRef<GpsOnly[] | undefined> {
    return httpResource(
      () => {
        const searchTerm = term?.();
        if (!searchTerm) return undefined;
        const params: HttpParams = this.buildContentParams(page, limit, sortDirection, searchTerm);
        return {
          url: `${environment.apiUrl}me/mapmodes`,
          method: 'GET',
          params,
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<GpsOnly[]>).data,
      },
    );
  }

  getCoachingContentsRessources(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
    term?: Signal<string | undefined>,
  ): HttpResourceRef<Coaching[] | undefined> {
    return httpResource(
      () => {
        const searchTerm = term?.();
        if (!searchTerm) return undefined;
        const params: HttpParams = this.buildContentParams(page, limit, sortDirection, searchTerm);
        return {
          url: `${environment.apiUrl}me/coachings`,
          method: 'GET',
          params,
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<Coaching[]>).data,
      },
    );
  }

  getContentDetailsResource(
    type: ContentType | undefined,
    id: string | null,
  ): HttpResourceRef<VideoSection | Coaching | GpsOnly | Workout | undefined> {
    return httpResource(
      () => {
        if (!type || !id) return undefined;
        const urlMap: Record<string, string> = {
          workout: `${environment.apiUrl}workout/${id}`,
          'gps-only': `${environment.apiUrl}mapmode/${id}`,
          coaching: `${environment.apiUrl}coaching/${id}`,
          scenic: `${environment.apiUrl}scenic/${id}`,
        };
        const url: string = urlMap[type];
        if (!url) return undefined;
        return { url, method: 'GET' };
      },
      {
        parse: (raw: unknown) =>
          (raw as ApiHttpResponse<VideoSection | Coaching | GpsOnly | Workout>).data,
      },
    );
  }

  getViewerRepartition(
    type: ContentType | undefined,
    id: string | null,
  ): HttpResourceRef<ApiHttpResponse<ViewerRepartitionItem[]> | undefined> {
    return httpResource(
      () => {
        if (!type || !id) return undefined;
        return {
          url: `${environment.apiUrl}creator/me/viewer-repartition/${id}`,
          method: 'GET',
          params: { content_type: type === 'gps-only' ? 'mapmode' : type },
        };
      },
      {
        parse: (raw: unknown) => {
          const response = raw as ApiHttpResponse<ViewerRepartitionItem[]>;
          return {
            ...response,
            data: groupViewerRepartition(response.data),
          };
        },
      },
    );
  }

  getContentStatistics(
    type: ContentType | undefined,
    id: string | null,
  ): HttpResourceRef<ContentStatistics | undefined> {
    return httpResource(
      () => {
        if (!type || !id) return undefined;

        return {
          url: `${environment.apiUrl}creator/me/content-statistics/${id}`,
          method: 'GET',
          params: { content_type: type === 'gps-only' ? 'mapmode' : type },
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<ContentStatistics>).data,
      },
    );
  }

  getContentCountries(
    type: ContentType | undefined,
  ): HttpResourceRef<ApiHttpResponse<ContentCountryItem[]> | undefined> {
    return httpResource(() => {
      if (!type) return undefined;
      return {
        url: `${environment.apiUrl}creator/me/video-countries`,
        method: 'GET',
        params: { content_type: type },
      };
    });
  }

  getContentUploadedDate(): HttpResourceRef<string[] | undefined> {
    return httpResource(
      () => ({
        url: `${environment.apiUrl}creator/me/upload-years`,
        method: 'GET',
      }),
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<string[]>).data,
      },
    );
  }

  getContentModerationRefusedReasons(
    id: string | null,
  ): HttpResourceRef<ModerationReason[] | undefined> {
    return httpResource(
      () => {
        if (!id) return undefined;

        return {
          url: `${environment.apiUrl}creator/${id}/get-content-moderation-refused-reason`,
          method: 'GET',
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<ModerationReason[]>).data,
      },
    );
  }

  getModerationRefusedReasons(): HttpResourceRef<ModerationReason[] | undefined> {
    return httpResource(
      () => {
        return {
          url: `${environment.apiUrl}creator/moderation-refused-reason`,
          method: 'GET',
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<ModerationReason[]>).data,
      },
    );
  }

  getPlaylistContentsRessources(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
  ): HttpResourceRef<Playlist[] | undefined> {
    return httpResource(
      () => {
        const params: Record<string, string | number> = {
          page: page,
          limit: limit,
          sortDirection: sortDirection,
        };
        return {
          url: `${environment.apiUrl}me/playlists`,
          method: 'GET',
          params,
        };
      },
      {
        parse: (raw: unknown) => (raw as ApiHttpResponse<Playlist[]>).data,
      },
    );
  }

  /**
   * Retourne la liste des uploads du client pour les __30dernier jours__.
   */
  public getUploadHistoryRessources: HttpResourceRef<UploadContentType[] | undefined> =
    httpResource(
      () => ({
        url: `${environment.apiUrl}creator/uploads/history`,
        method: 'GET',
      }),
      {
        parse: (raw: unknown): UploadContentType[] =>
          (raw as ApiHttpResponse<RawUploadContentType[]>).data.map(
            (upload: RawUploadContentType) => ({
              ...upload,
              content_type: upload.content_type === 'mapmode' ? 'gps-only' : upload.content_type,
            }),
          ),
      },
    );

  /**
   * Retourne la liste des themes pour un workout.
   */
  public getTrainingThemeRessources: HttpResourceRef<string[] | undefined> = httpResource(
    () => ({
      url: `${environment.apiUrl}workout/themes`,
      method: 'GET',
    }),
    {
      parse: (raw: unknown) => (raw as ApiHttpResponse<string[]>).data,
    },
  );

  private buildContentParams(
    page: number,
    limit: number,
    sortDirection: SortDirectionType,
    term?: string,
  ): HttpParams {
    let params: HttpParams = new HttpParams({ encoder: new BracketHttpParameterCodec() })
      .set('page', page)
      .set('limit', limit)
      .set('sortDirection', sortDirection);

    if (term) {
      params = params.set('term', term);
    }

    return params;
  }
}

export class BracketHttpParameterCodec implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key).replace(/%5B/g, '[').replace(/%5D/g, ']');
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
